import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatError } from '../lib/api';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, Mail, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import SoulBalmLogo from '../components/SoulBalmLogo';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      navigate('/home');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.response?.data?.message || 'Unable to connect. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.includes('@')) { setForgotError('Please enter a valid email address.'); return; }
    setForgotLoading(true);
    setForgotError('');
    try {
      const { data } = await api.post('/auth/forgot-password', { email: forgotEmail, origin_url: window.location.origin });
      if (data.email_failed) {
        setForgotError(data.message);
      } else {
        setForgotSent(true);
      }
    } catch (e) {
      setForgotError('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const redirectUri = window.location.origin + '/auth/callback';
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&access_type=offline&prompt=consent`;
    window.location.href = googleAuthUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070A1A] px-4" data-testid="login-page">
      <div className="absolute inset-0 mystic-gradient" />
      <motion.div className="relative z-10 w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="text-center mb-8">
          <SoulBalmLogo size={64} className="mx-auto mb-6" />
          <h1 className="font-['Playfair_Display'] text-3xl text-[#FFFFF0] mb-2">Welcome back.</h1>
          <p className="text-sm text-[#BDB2C4] font-['Playfair_Display'] italic">The cards have been waiting.</p>
        </div>
        <form onSubmit={handleSubmit} className="glass-card p-8 space-y-5">
          <Input data-testid="login-email" type="text" placeholder="Email or username" value={email}
            onChange={e => setEmail(e.target.value)}
            className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 w-full bg-white/5 border-white/10 focus:border-[#D4AF37]" />
          <div className="relative">
            <Input data-testid="login-password" type={showPass ? "text" : "password"} placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)}
              className="glass-input h-12 rounded-xl px-4 pr-12 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 w-full bg-white/5 border-white/10 focus:border-[#D4AF37]" />
            <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-3 text-[#BDB2C4]" data-testid="login-toggle-password">
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="login-remember-me-label">
              <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-[#D4AF37]/40 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]"
                data-testid="login-remember-me" />
              <span className="text-xs text-[#BDB2C4]">Remember Me</span>
            </label>
            <button type="button" onClick={() => { setShowForgot(true); setForgotSent(false); setForgotError(''); setForgotEmail(''); }}
              className="text-xs text-[#C87886] hover:text-[#D4AF37] transition-colors" data-testid="forgot-password-btn">
              Forgot Password?
            </button>
          </div>
          {error && <p className="text-sm text-[#C87886]" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading || !email || !password}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="login-submit-btn">
            {loading ? 'Opening the door...' : 'Enter the Room'} <ArrowRight size={16} />
          </button>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center"><span className="bg-[#0F0B1E] px-4 text-xs text-[#BDB2C4]/60">or</span></div>
          </div>

          <button type="button" onClick={handleGoogleSignIn} data-testid="google-signin-btn"
            className="w-full h-12 rounded-xl flex items-center justify-center gap-3 bg-white/5 border border-white/10 hover:border-[#D4AF37]/40 hover:bg-white/10 transition-all duration-300 text-[#FFFFF0] text-sm">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"/></svg>
            Sign in with Google
          </button>

          <p className="text-center text-sm text-[#BDB2C4]">
            First time here? <Link to="/signup" className="text-[#D4AF37] hover:underline" data-testid="login-signup-link">Begin your journey</Link>
          </p>
        </form>
      </motion.div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="forgot-password-modal">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-8 max-w-md w-full">
            {!forgotSent ? (
              <>
                <h3 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-3 text-center">Reset Your Password</h3>
                <p className="text-sm text-[#BDB2C4] mb-6 text-center">Enter your email address and we'll send you a link to reset your password.</p>
                <div className="relative mb-4">
                  <Mail size={16} className="absolute left-4 top-4 text-[#BDB2C4]" />
                  <Input data-testid="forgot-email-input" type="email" placeholder="Your email address" value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    className="glass-input h-12 rounded-xl pl-10 pr-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 w-full bg-white/5 border-white/10 focus:border-[#D4AF37]" />
                </div>
                {forgotError && <p className="text-xs text-[#C87886] mb-3">{forgotError}</p>}
                <button onClick={handleForgotPassword} disabled={forgotLoading || !forgotEmail}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-30 mb-3" data-testid="forgot-send-btn">
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button onClick={() => setShowForgot(false)} className="text-sm text-[#BDB2C4] hover:text-[#FFFFF0] transition-colors w-full text-center" data-testid="forgot-password-close">
                  Back to Login
                </button>
              </>
            ) : (
              <div className="text-center">
                <CheckCircle size={48} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-3">Check Your Email</h3>
                <p className="text-sm text-[#BDB2C4] mb-2">We've sent a password reset link to:</p>
                <p className="text-sm text-[#D4AF37] mb-4">{forgotEmail}</p>
                <p className="text-xs text-[#BDB2C4] mb-6">If you don't see the email, check your spam folder. The link expires in 1 hour.</p>
                <button onClick={() => setShowForgot(false)} className="btn-primary" data-testid="forgot-password-close">
                  Back to Login
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
